/**
 * @file Contains common validation rules that have no heavy dependencies.
 */
import { msgf, msg } from '@/services/isomorphic/I18NService';
import type { IValidationResult, Validation } from '../validatable';
import { forms_error_mustBeTrue } from "@/lang/__generated__/ahnu/forms_error_mustBeTrue";
import { forms_error_notEmpty } from "@/lang/__generated__/ahnu/forms_error_notEmpty";
import { forms_error_generic_invalidValue } from "@/lang/__generated__/ahnu/forms_error_generic_invalidValue";
import { forms_error_onlyNumeric } from "@/lang/__generated__/ahnu/forms_error_onlyNumeric";
import { forms_error_onlyLetters } from "@/lang/__generated__/ahnu/forms_error_onlyLetters";
import { forms_error_minLength } from "@/lang/__generated__/ahnu/forms_error_minLength";
import { forms_error_maxLength } from "@/lang/__generated__/ahnu/forms_error_maxLength";
import { forms_error_containsUpper } from "@/lang/__generated__/ahnu/forms_error_containsUpper";
import { forms_error_containsLower } from "@/lang/__generated__/ahnu/forms_error_containsLower";
import { forms_error_containsNumber } from "@/lang/__generated__/ahnu/forms_error_containsNumber";
import { forms_error_containsSpecialChar } from "@/lang/__generated__/ahnu/forms_error_containsSpecialChar";
import { giftcard_purchasePage_amountIntegerError } from "@/lang/__generated__/ahnu/giftcard_purchasePage_amountIntegerError";
import { giftcard_purchasePage_amountError } from "@/lang/__generated__/ahnu/giftcard_purchasePage_amountError";

/**
 * Validates that the input is not empty (after being trimmed).
 *
 * @param name - Resource string.
 * @param property - Property key.
 * @param value - The value to validate.
 * @returns Whether the property is valid and an error message.
 */
export const nonEmpty: Validation<string> = (
  name: string,
  property: string,
  value: string
): IValidationResult => {
  if (value.trim().length === 0) {
    return {
      isValid: false,
      errorMessage: msgf(forms_error_notEmpty, { name: name.toLowerCase() })
    };
  }

  return { isValid: true, errorMessage: null };
};

/**
 * Will validate that the value of property matches the provided regular
 * expression.
 *
 * @param regex - The regular expression to test the value with.
 * @param message - A function to generate a custom error message for when the
 * validation fails.
 *
 * @returns Whether the property is valid and an error message.
 */
export const matchesRegex =
  (regex: RegExp, message?: () => string): Validation<string> =>
  (name: string, property: string, value: string): IValidationResult => {
    if (regex.test(value)) {
      return { isValid: true, errorMessage: null };
    }

    return {
      isValid: false,
      errorMessage: message
        ? message()
        : msgf(forms_error_generic_invalidValue, { name: name.toLowerCase() })
    };
  };

/**
 * Validates that the input is numeric.
 *
 * @param name - Resource string.
 * @param property - Property key.
 * @param value - The value to validate.
 * @returns Whether the property is valid and an error message.
 */
export const onlyNumeric: Validation<string> = (
  name: string,
  property: string,
  value: string
): IValidationResult => {
  // Pass if empty
  if (value.length === 0) {
    return { isValid: true, errorMessage: null };
  }

  const isValid = /^[0-9]+$/.test(value);
  return {
    isValid,
    errorMessage: msgf(forms_error_onlyNumeric, { name: name.toLowerCase() })
  };
};

/**
 * Validates that the input is only letters.
 *
 * @param name - Resource string.
 * @param property - Property key.
 * @param value - The value to validate.
 * @returns Whether the property is valid and an error message.
 */
export const onlyLetters: Validation<string> = (
  name: string,
  property: string,
  value: string
): IValidationResult => {
  const isValid = /^[A-Za-z\s]*$/.test(value);
  return {
    isValid,
    errorMessage: isValid
      ? null
      : msgf(forms_error_onlyLetters, { name: name.toLowerCase() })
  };
};

/**
 * Given a number `n`, creates a validation function that will only allow
 * strings with `n` characters or more.
 *
 * **IMPORTANT:** `minLength` will NOT fail empty strings. This is to not fail
 * optional properties when not specified. If the value cannot be empty, include
 * {@link nonEmpty `nonEmpty`} as a validation too.
 *
 * @param n - Minimum length.
 *
 * @returns Whether the property is valid and an error message.
 */
export const minLength = (n: number): Validation<string> => {
  return (name: string, property: string, value: string): IValidationResult => {
    // Ignore if the string is empty.
    if (value.length === 0) {
      return { isValid: true, errorMessage: null };
    }

    if (value.length < n) {
      return {
        isValid: false,
        errorMessage: msgf(forms_error_minLength, {
          name: name.toLowerCase(),
          length: n
        })
      };
    }

    return { isValid: true, errorMessage: null };
  };
};

/**
 * Factory of validation. Given `n` creates a validation function
 * that will validate strings for maximum of n characters.
 * @param n - Max length.
 * @returns Whether the property is valid and an error message.
 */
export const maxLength = (n: number): Validation<string> => {
  return (name: string, property: string, value: string): IValidationResult => {
    if (value.length > n) {
      return {
        isValid: false,
        errorMessage: msgf(forms_error_maxLength, {
          name: name.toLowerCase(),
          length: n
        })
      };
    }

    return { isValid: true, errorMessage: null };
  };
};

/**
 * Validates that the input contains an uppercase character.
 *
 * @param name - Resource string.
 * @param property - Property key.
 * @param value - The value to validate.
 * @returns Whether the property is valid and an error message.
 */
export const containsUpper: Validation<string> = (
  name: string,
  property: string,
  value: string
): IValidationResult => {
  const isValid = /[A-Z]/.test(value);
  return {
    isValid,
    errorMessage: isValid
      ? null
      : msgf(forms_error_containsUpper, { name: name.toLowerCase() })
  };
};

/**
 * Validates that the input contains a lowercase character.
 *
 * @param name - Resource string.
 * @param property - Property key.
 * @param value - The value to validate.
 * @returns Whether the property is valid and an error message.
 */
export const containsLower: Validation<string> = (
  name: string,
  property: string,
  value: string
): IValidationResult => {
  const isValid = /[a-z]/.test(value);
  return {
    isValid,
    errorMessage: isValid
      ? null
      : msgf(forms_error_containsLower, { name: name.toLowerCase() })
  };
};

/**
 * Validates that the input contains at least one numeric digit.
 *
 * @param name - Resource string.
 * @param property - Property key.
 * @param value - The value to validate.
 * @returns Whether the property is valid and an error message.
 */
export const containsNumber: Validation<string> = (
  name: string,
  property: string,
  value: string
): IValidationResult => {
  const isValid = /[0-9]/.test(value);
  return {
    isValid,
    errorMessage: isValid
      ? null
      : msgf(forms_error_containsNumber, { name: name.toLowerCase() })
  };
};

/**
 * Validates that the input contains at least one special character.
 * Allowed special characters: $%/()[]{}=?!.,-_*|+~#@.
 *
 * @param name - Resource string.
 * @param property - Property key.
 * @param value - The value to validate.
 * @returns Whether the property is valid and an error message.
 */
export const containsSpecialChar: Validation<string> = (
  name: string,
  property: string,
  value: string
): IValidationResult => {
  const isValid = /[$%/()[\]{}=?!.,\-_*|+~#@]/.test(value);
  return {
    isValid,
    errorMessage: isValid
      ? null
      : msgf(forms_error_containsSpecialChar, { name: name.toLowerCase() })
  };
};

/**
 * Validates that the input is a true value. This is used
 * for mandatory agreement.
 *
 * @param name - Resource string.
 * @param property - Property key.
 * @param value - The value to validate.
 * @returns Whether the property is valid and an error message.
 */
export const mustBeTrue: Validation<boolean> = (
  name: string,
  property: string,
  value: boolean
): IValidationResult => {
  return {
    isValid: value,
    errorMessage: value ? null : msg(forms_error_mustBeTrue)
  };
};

/**
 * This is used for mandatory opt-in (Checkbox).
 *
 * @param message - A function to generate a custom error message for when the
 * validation fails.
 * @returns Whether the property is valid and an error message.
 */
export const requiredOptIn =
  (message: () => string): Validation<boolean> =>
  (name: string, property: string, value: boolean): IValidationResult => {
    if (!value) {
      return { isValid: false, errorMessage: message() };
    }

    return { isValid: true, errorMessage: null };
  };

/**
 * Validates that the input is an integer number.
 *
 * **IMPORTANT:** `isInteger` will NOT fail empty strings. This is to not fail
 * optional properties when not specified.
 *
 * @param name - Resource string.
 * @param property - Property key.
 * @param value - The value to validate.
 * @returns Whether the property is valid and an error message.
 */
export const isInteger: Validation<string> = (
  name: string,
  property: string,
  value: string
): IValidationResult => {
  // Ignore if the string is empty.
  if (value.length === 0) {
    return { isValid: true, errorMessage: null };
  }

  const amount = parseFloat(value.replace(/[^0-9.]/g, ''));

  if (Number.isNaN(amount)) {
    return { isValid: true, errorMessage: null };
  }

  if (!Number.isInteger(amount)) {
    const lower = Math.floor(amount);
    const upper = Math.ceil(amount);

    return {
      isValid: false,
      errorMessage: msgf(giftcard_purchasePage_amountIntegerError, {
        lower,
        upper
      })
    };
  }

  return { isValid: true, errorMessage: null };
};

/**
 * Given min and max numbers, creates a validation function that will only allow
 * numeric strings within the specified range.
 *
 * **IMPORTANT:** `inRange` will NOT fail empty strings. This is to not fail
 * optional properties when not specified.
 *
 * @param min - Minimum value (inclusive).
 * @param max - Maximum value (inclusive).
 * @param message - Optional custom error message function.
 *
 * @returns Whether the property is valid and an error message.
 */
export const inRange =
  (min: number, max: number, message?: () => string): Validation<string> =>
  (name: string, property: string, value: string): IValidationResult => {
    // Ignore if the string is empty.
    if (value.length === 0) {
      return { isValid: true, errorMessage: null };
    }

    const amount = parseFloat(value.replace(/[^0-9.]/g, ''));

    if (Number.isNaN(amount)) {
      return { isValid: true, errorMessage: null };
    }

    if (amount < min || amount > max) {
      return {
        isValid: false,
        errorMessage: message
          ? message()
          : msgf(giftcard_purchasePage_amountError, {
              lower: min,
              upper: max
            })
      };
    }

    return { isValid: true, errorMessage: null };
  };
